/**
 * Shared types for the Candidate Recommendations feature (Story 2a).
 *
 * This file is the single source of truth consumed by every lane building this
 * page (foundation, filters/controls, results, detail modal). Do NOT duplicate
 * or fork these shapes elsewhere — import from here.
 */

// 'collab' = Collaborated Search (kan-82): similarity to the candidates already
// interviewed on the selected job order. Never a persisted mode — see
// SavedSearchSettingsV1.mode below.
export type TabKey = 'batch' | 'search' | 'collab' | 'shortlist' | 'declined';
export type SearchMode = 'boolean' | 'semantic' | 'hybrid';
export type ScoreBand = 'strong' | 'moderate' | 'weak';
// LLM match band (Batch Matches only) — derived client-side from
// candidate.explain.llmScore via utils.matchBand(). See utils.ts for the
// 70/50 thresholds. Distinct from ScoreBand, which is embedding-similarity
// based and drives Search Pool.
export type MatchBand = 'strong' | 'possible' | 'none';

// ---- recsys dispositions (Story 2b) ----
// Backed by the new RecsysDispositions table, keyed (jobOrderId, candidateId).
// Only Shortlist/Decline live here; "Apply" creates a real JobApplicant via
// the existing createSubmissionOnApply path and is NOT a disposition.
export type RecsysDispositionStatus = 'SHORTLISTED' | 'DECLINED';
export interface RecsysDisposition {
  id: string;
  jobOrderId: string;
  candidateId: string;
  status: RecsysDispositionStatus;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---- recsys wire types (proxied /recsys/*) ----
// NOTE: recsys still accepts a `seniority` filter, but the UI no longer exposes
// it (the Gemini tech-ladder bands don't map to real estate) — years-of-
// experience is the seniority proxy instead.
export interface RecsysFilters { geo?: { lat: number; lng: number; radiusKm: number }; minYearsExperience?: number; skills?: string[]; }
export interface RecsysExplain { status: 'ok' | 'timeout' | 'error' | 'unparseable'; llmScore: number | null; explanation: string | null; gaps: string[]; }
export interface RecsysMatchCandidate { candidateId: string; score: number | null; maxSim: number | null; meanTop3: number | null; matchedChunks?: { chunkId: string; seq: number; sim: number }[]; explain?: RecsysExplain; }
// ---- applicant pool (kan-119) ----
// "Search in: New applicants" restricts every search tab to the selected job
// order's JobApplicants in the given statuses. The page sends ONLY this
// descriptor — the ATS proxy resolves it to candidate ids server-side (behind
// the job-access check) and forwards them to recsys as filters.candidateIds,
// so the browser never ships the id list. statuses omitted = ['Applied'].
export interface RecsysApplicantPool { jobOrderId: string; statuses?: string[]; }
// Echoed back by the proxy on any pool-restricted response. applicants = the
// pool's size in ATS; truncated = the proxy capped the id list it forwarded
// (newest applications first). A pool with zero applicants short-circuits in
// the proxy: { candidates: [], stats: {}, pool } — recsys is never called.
export interface RecsysPoolSummary { jobOrderId: string; statuses: string[]; applicants: number; truncated: boolean; }
// recsys stats on a pool-restricted call: poolRequested = ids the proxy sent,
// poolInCorpus = those present in the served corpus (before other filters).
// Both null (or absent) without a pool. The index signature carries every
// other stats field through untyped, as before.
export interface RecsysPoolStats { poolRequested?: number | null; poolInCorpus?: number | null; [k: string]: unknown; }
export interface RecsysMatchRequest { jobDescription: string; topN?: number; filters?: RecsysFilters; mustHaveTerms?: string[]; explain?: boolean; useBrief?: boolean; applicantPool?: RecsysApplicantPool; }
export interface RecsysMatchResponse { candidates: RecsysMatchCandidate[]; stats?: RecsysPoolStats; pool?: RecsysPoolSummary; }
export interface RecsysSearchRequest { query: string; mode: SearchMode; filters?: RecsysFilters; topN?: number; applicantPool?: RecsysApplicantPool; }
export interface RecsysSearchCandidate { candidateId: string; score: number | null; maxSim: number | null; meanTop3: number | null; updatedAt: string | null; }
export interface RecsysSearchResponse { candidates: RecsysSearchCandidate[]; stats?: RecsysPoolStats; pool?: RecsysPoolSummary; }
// Collaborated Search (kan-82): POST /recsys/similar. Seeds are the candidates
// already interviewed on the job order (frontend caps the list at 15; recsys
// rejects >16). No mustHaveTerms and no explain on this path — results are
// similarity-ranked only. `score` on the returned candidates is the FUSED RRF
// value (a rank scale, not a 0..1 similarity) — index.tsx normalizes rows onto
// maxSim for display; ordering stays the server's array order.
export interface RecsysSimilarRequest { seedCandidateIds: string[]; filters?: RecsysFilters; topN?: number; applicantPool?: RecsysApplicantPool; }
// seedsUsed = seeds with vectors in the serving state; skippedSeedIds = seeds
// absent from the corpus. Index signature carries the remaining stats fields
// (timings/manifestVersion) through untyped. NB the proxy's empty-pool
// short-circuit (kan-119) answers with `stats: {}`, so callers must not trust
// seedsUsed to be present on a pool-restricted call.
export interface RecsysSimilarStats { seedsRequested: number; seedsUsed: number; skippedSeedIds: string[]; poolRequested?: number | null; poolInCorpus?: number | null; [k: string]: unknown; }
export interface RecsysSimilarResponse { candidates: RecsysMatchCandidate[]; stats: RecsysSimilarStats; pool?: RecsysPoolSummary; }
export interface RecsysJobMatches { jobId: string; generatedAt: string; computedAt: string; manifestVersion: string; briefUsed: boolean; candidates: { candidateId: string; rank: number; score: number | null; maxSim: number | null; meanTop3: number | null; matchedChunkIds: string[] }[]; }
export interface RecsysProfile { candidateId: string; manifestVersion: string; attributes: { currentTitle: string | null; seniority: string | null; yearsExperience: number | null; city: string | null; state: string | null; country: string | null; lat: number | null; lng: number | null; skills: string[]; certifications: string[]; updatedAt: string | null; nameRedacted: boolean; latestResumeId: string | null }; resume: { resumeId: string; url: string; expiresAt: string } | null; }
export interface RecsysIngestStatus { manifestVersion: string; embedModel: string; generatedAt: string; loadedAt: string; ageSeconds: number; staleThresholdSeconds: number; stale: boolean; counts: Record<string, number>; refresh?: unknown; }
export class RecsysHttpError extends Error { constructor(public status: number, public body?: unknown) { super('recsys ' + status); } }

// ---- recsys client (recsysClient.ts) ----
export interface RecsysClient {
  match(body: RecsysMatchRequest): Promise<RecsysMatchResponse>;
  search(body: RecsysSearchRequest): Promise<RecsysSearchResponse>;
  similar(body: RecsysSimilarRequest): Promise<RecsysSimilarResponse>;
  candidateProfile(id: string): Promise<RecsysProfile>; // throws RecsysHttpError(404) if removed
  jobMatches(jobId: string): Promise<RecsysJobMatches>;  // throws RecsysHttpError(404) until first batch
  ingestStatus(): Promise<RecsysIngestStatus>;
}

// ---- ATS candidatesByIds GraphQL row (subset selected by CANDIDATES_BY_IDS) ----
export interface AtsResume { id: string; resumeFileName?: string | null; originalUrl?: string | null; pdfVersionUrl?: string | null; createdAt?: string | null; }
// Shaped to match what CandidateWorkspacePanel (the reused /new-applicants modal)
// reads off applicant.candidate. CandidateRow only uses fullName/jobTitle/companyName.
export interface AtsCandidate { id: string; fullName: string; jobTitle: string | null; companyName: string | null; linkedInUrl: string | null; emails: { email: string; isPrimary: boolean }[]; phones: { phoneNumber: string; isPrimary: boolean }[]; resume: AtsResume[]; recruiterOwner: { id: string; fullName: string } | null; candidateStats: { ownershipExpiration: string | null; recruitingOwner: { id: string; fullName: string } | null }[] | null; }

// ---- unified row model (both batch + search normalize into this) ----
// llmScore/judged/matchBand are Batch-only (LLM-score-driven matching);
// score/band/maxSim remain the embedding-similarity values that still drive
// Search Pool and serve as the Batch tie-break/debug value. Search/Shortlist/
// Declined rows set llmScore/matchBand to null and judged to false.
export interface NormalizedRow { candidateId: string; rank: number; score: number | null; band: ScoreBand | null; maxSim: number | null; meanTop3: number | null; matchedChunkCount: number; isBooleanMatch: boolean; updatedAt: string | null; explain?: RecsysExplain; llmScore: number | null; judged: boolean; matchBand: MatchBand | null; }

// ---- applicant-mode row data (kan-119) ----
// The candidate's JobApplicant row on the selected job order (newest if
// several), from JOB_APPLICANTS_FOR_CANDIDATES. Only fetched in applicant mode.
// JobAppStatus is the GraphQL enum KEY (e.g. 'APPLIED', 'CALLED') — the same
// form CandidateWorkspacePanel / SET_STATUS_OPTIONS use.
// aiScoreReason/aiScoredResumeId/aiScoredAt (kan-120) feed the row's
// AiScoreBadge so a scored applicant opens the full score modal.
export interface RecsysRowApplicant {
  id: string;
  JobAppStatus: string;
  createdAt: string;
  aiScore: number | null;
  aiScoreReason: string | null;
  aiScoredResumeId: string | null;
  aiScoredAt: string | null;
}
// What AiScoreBadge reports after a fetch / rescore (kan-120).
export interface ApplicantScoreUpdate { aiScore: number; aiScoreReason: string; aiScoredResumeId: string; aiScoredAt: string; }

// ---- hydration state (progressive: list -> ats -> profile) ----
// applicant is the third, applicant-mode-only source (kan-119);
// applicantLoaded flips once that lookup settles either way.
export interface HydratedRow { row: NormalizedRow; ats?: AtsCandidate; atsLoaded: boolean; profile?: RecsysProfile; profileStatus: 'idle' | 'loading' | 'loaded' | 'error' | 'removed'; applicant?: RecsysRowApplicant; applicantLoaded?: boolean; }

// ---- "Search in" scope (kan-119) ----
// 'all' = the whole corpus (the page's original behavior); 'applicants' = the
// selected job order's new (Applied) applicants only. Session-only — never
// persisted, reset to 'all' whenever the job order changes.
export type PoolScope = 'all' | 'applicants';
// What the last pool-restricted run reported, for the "N of M applicants
// searchable" line: applicants = M (pool size in ATS), inCorpus = N (of the
// ids sent, how many recsys has résumés for), truncated = the proxy capped
// the list it forwarded.
export interface PoolInfo { applicants: number; inCorpus: number; truncated: boolean; }
// Row actions/labels mode. 'applicants' (kan-119) = pool tabs in applicant
// mode: no Shortlist/Decline/Apply row buttons, the row shows the application
// date + JobAppStatus pill instead and actions live in the modal.
export type RowDispositionMode = 'shortlist' | 'declined' | 'applicants' | null;

// ---- filter UI state ----
// geoCenterCustom / radiusCustom mark a recruiter-set center / radius: while
// the same job order stays selected they suppress the job-derived prefill in
// context.tsx (selecting a different job resets both and re-prefills).
export interface FilterState { minYears: number; skills: string[]; geoCenterLabel: string; geoCenterCustom: boolean; lat: number | null; lng: number | null; radiusMi: number; /* radiusMi max sentinel = 'Any distance' */ radiusCustom: boolean; }

// ---- radius-center resolution (kan-83) ----
// The radius center comes from the text in the rail's location box, resolved
// through the ATS location service (RESOLVE_LOCATION -> UtilsService
// .locationFinder). Deliberately NOT part of FilterState: it is session-only
// UI state and must never reach the kan-80 saved-settings row.
//  - idle      empty box, no geo filter at all
//  - resolving a resolution is in flight (the radius slider is disabled)
//  - resolved  filters.lat/lng hold usable coordinates (includes the silent
//              fallback to the job's stored coordinates after a failed call)
//  - noMatch   the service answered "no usable match" — no fallback. The
//              service cannot distinguish that from a Google-side failure.
//  - error     the call itself failed AND the job has no stored coordinates
export type GeoResolveStatus = 'idle' | 'resolving' | 'resolved' | 'noMatch' | 'error';
/** Wire shape of the resolveLocation query (packages/api job-order/location-resolve.resolver.ts). */
export interface ResolvedLocation { latitude: number; longitude: number; formattedAddress: string | null; }

// ---- per-job-order saved search settings (kan-80) ----
// The page's complete search configuration, persisted against the JOB ORDER
// (shared by every user who can access it) on every executed search and loaded
// back when that job order is selected. schemaVersion is written for future
// readers; the sanitizer in utils.ts spreads whatever it finds over the page
// defaults and drops unknown fields, so rows written by an older version of the
// page keep loading after new filters ship.
export const SEARCH_SETTINGS_SCHEMA_VERSION = 1;
export interface SavedSearchSettingsV1 {
  schemaVersion: number;
  // Active mode at execution time. Collaborated Search (kan-82) persists
  // nothing, so 'collab' is deliberately NOT a value here — the sanitizer in
  // utils.ts maps any non-'search' stored mode to 'batch'.
  mode: 'batch' | 'search';
  topN: number;
  mustHave: string[];
  filters: FilterState;
  search: { mode: SearchMode; query: string };
}
/** Live page state the payload is built from (see buildSearchSettingsPayload). */
export type SavedSearchSettingsState = Omit<SavedSearchSettingsV1, 'schemaVersion'>;

// ---- shared context value (context.tsx) consumed by every Stage-1 component ----
export interface RecPageState { view: TabKey; selectedCompanyId: string | null; jobId: string | null; jobDescription: string; jobCenter: { lat: number | null; lng: number | null; label: string }; topN: number; mustHave: string[]; filters: FilterState; search: { mode: SearchMode; query: string }; page: number; detailId: string | null; resumeViewId: string | null; ingest: RecsysIngestStatus | null; }

// ---- shared component prop contracts (Stage-1 lanes MUST implement these exact exports) ----
// Lane C:
// onCenterCommit fires on BLUR / ENTER of the location box only (never per
// keystroke — every commit costs a geocode); onChange keeps carrying the
// keystrokes as a label-only edit. geoResolveStatus / geoFormattedAddress
// drive the message under the radius slider.
export interface FiltersRailProps { filters: FilterState; onChange: (patch: Partial<FilterState>) => void; onCenterCommit: (text: string) => void; onClearAll: () => void; radiusDisabled: boolean; geoResolveStatus: GeoResolveStatus; geoFormattedAddress: string | null; }
export interface FreshnessPillProps { ingest: RecsysIngestStatus | null; }
export interface StaleBannerProps { ingest: RecsysIngestStatus | null; onRefresh: () => void; }
export interface BatchControlCardProps { topN: number; onTopN: (n: number) => void; mustHave: string[]; onMustHave: (t: string[]) => void; briefUsed: boolean; computedAt: string | null; onRefresh: () => void; loading: boolean; }
export interface SearchModeControlProps { mode: SearchMode; query: string; topN: number; onMode: (m: SearchMode) => void; onQuery: (q: string) => void; onTopN: (n: number) => void; onSearch: () => void; }
// Lane D:
// Story 2b optional additions below are backward-compatible: existing 2a call
// sites (Batch/Search, no dispositionMode) keep working unchanged.
export interface ResultsPanelProps {
  view: TabKey;
  hydrated: HydratedRow[];
  total: number;
  page: number;
  onPage: (p: number) => void;
  loading: boolean;
  tooBroad: boolean;
  // The last run failed at the search backend (recsys 5xx / proxy 502 / network)
  // rather than returning an empty list — rendered as an explicit error card,
  // never as "No matches found" (kan-119 follow-up). See utils.ts
  // SearchBackendError; duringMaintenance switches the copy to the nightly window.
  backendError?: { status: number | null; duringMaintenance: boolean } | null;
  // Batch Matches is explicit-run: true when a job is selected but the recruiter
  // hasn't hit Refresh yet, so the panel shows a "hit Refresh" prompt instead of
  // the misleading "No matches found" empty state.
  awaitingRun?: boolean;
  // Collaborated Search (kan-82): true when the run was gated because the job
  // order has no interviews at all — a distinct empty state from "no matches".
  noInterviews?: boolean;
  // Collaborated Search seed accounting for the header indicator ("Based on N
  // of M interviewed candidates."): used = seeds recsys found vectors for
  // (stats.seedsUsed), total = ALL interviewed candidates on the job order
  // (pre-slice — may exceed the ≤15 sent). null on every other tab.
  collabSeeds?: { used: number; total: number } | null;
  // Batch-only match/non-match partition (LLM-score-driven matching):
  // matchCount is the count of rows sorted ahead of the "Show N non-matches"
  // expander; nonMatchCount = hydrated total - matchCount for the current
  // view. Ignored by Search/Shortlist/Declined.
  matchCount: number;
  nonMatchCount: number;
  showNonMatches: boolean;
  onToggleNonMatches: () => void;
  onOpenDetail: (candidateId: string) => void;
  // Shortlist tab bulk-select.
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  // Shortlist/Declined row actions; null/undefined = Story 2a Add/Decline behavior.
  // 'applicants' (kan-119) = applicant mode — see RowDispositionMode.
  dispositionMode?: RowDispositionMode;
  onApplyRow?: (id: string) => void;
  onRestoreRow?: (id: string) => void;
  onShortlistRow?: (id: string) => void;
  onDeclineRow?: (id: string) => void;
  // Passed through to CandidateRow (kan-120 applicant-mode AI score badge).
  onApplicantScoreUpdated?: CandidateRowProps['onApplicantScoreUpdated'];
}
export interface CandidateRowProps {
  item: HydratedRow;
  view: TabKey;
  onOpenDetail: (id: string) => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  dispositionMode?: RowDispositionMode;
  onApplyRow?: (id: string) => void;
  onRestoreRow?: (id: string) => void;
  onShortlistRow?: (id: string) => void;
  onDeclineRow?: (id: string) => void;
  // Applicant mode (kan-120): the row's AiScoreBadge fetched / rescored, so the
  // hydrated row is written back to agree with the badge's local state.
  onApplicantScoreUpdated?: (candidateId: string, score: ApplicantScoreUpdate) => void;
}
// Lane E:
export interface RecommendationDetailModalProps { open: boolean; onClose: () => void; item: HydratedRow | null; }
